/**
 * A marca do Zunox — losangos e chevrons em duas cores.
 *
 * O desenho é de duas cores por construção: um azul fixo, que é a cor da
 * marca e não se altera com o tema, e uma segunda cor que precisa de inverter
 * (tinta escura sobre fundo claro, quase branco sobre fundo escuro). Essa
 * segunda cor entra por `currentColor`, portanto quem monta a marca escolhe-a
 * com uma classe de texto normal (`text-foreground`, `text-white`) e não há
 * nenhum `isDark ? … : …` aqui dentro.
 *
 * O azul é um hex literal e é a única excepção legítima à regra de não haver
 * hexes em componentes: uma marca não muda de cor com o tema da aplicação, e
 * ligá-la a `--primary` faria o logótipo mudar sempre que alguém afinasse a
 * cor de acção.
 *
 * The canvas for this component is at
 * tempo/designs/design-system/zunox/index.canvas.tsx. If you adjust this
 * component in any way, ensure the canvas and its asset declaration stay
 * consistent.
 */

import type { CSSProperties } from "react";

/**
 * As cores da marca, por referência ao token e não por literal.
 *
 * Os valores vivem em `globals.css` (`--brand-*`). Um hex escrito aqui dentro
 * não se encontrava quando fosse preciso mudá-lo, e o guarda de tokens
 * (`scripts/check-design-tokens.mjs`) marcava-o como violação — com razão:
 * ele não tem como distinguir um hex de marca de um hex esquecido.
 */
export const BRAND_BLUE = "var(--brand-blue)";

/**
 * A segunda cor da marca, a que inverte. Entra normalmente por
 * `currentColor`; estas constantes existem para quem monta a marca fora do
 * fluxo de tema (uma capa, um cabeçalho de e-mail) e tem de a nomear.
 */
export const BRAND_INK_ON_LIGHT = "var(--brand-ink-on-light)";
export const BRAND_INK_ON_DARK = "var(--brand-ink-on-dark)";

/**
 * A mesma tinta, mas deixando o tema escolher qual — é esta que serve a quem
 * monta a marca dentro da aplicação e não quer nem o `text-foreground` (que é
 * a cor do texto, não a da marca) nem um ramo em JavaScript.
 *
 * O ramo em JavaScript é a armadilha que isto fecha: `resolvedTheme` só
 * responde onde houver um `ThemeProvider` montado, e no anfitrião das canvases
 * não há — o ecrã julgava-se escuro e punha tinta quase branca sobre o fundo
 * claro. A cascata do CSS não tem esse problema.
 */
export const BRAND_INK = "var(--brand-ink)";

export interface ZunoxMarkProps {
  className?: string;
  style?: CSSProperties;
  /** Rótulo acessível. `null` marca a imagem como decorativa. */
  title?: string | null;
}

export function ZunoxMark({
  className,
  style,
  title = "Zunox",
}: ZunoxMarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 98.37 49.69"
      className={className}
      style={style}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <rect
        fill="currentColor"
        x="41.3"
        y="3.27"
        width="15.77"
        height="15.77"
        transform="translate(22.29 -31.51) rotate(45)"
      />
      <rect
        fill="currentColor"
        x="41.3"
        y="30.64"
        width="15.77"
        height="15.77"
        transform="translate(41.65 -23.5) rotate(45)"
      />
      <rect
        fill={BRAND_BLUE}
        x="54.99"
        y="16.95"
        width="15.77"
        height="15.77"
        transform="translate(35.98 -37.18) rotate(45)"
      />
      <rect
        fill={BRAND_BLUE}
        x="27.61"
        y="16.95"
        width="15.77"
        height="15.77"
        transform="translate(27.96 -17.83) rotate(45)"
      />
      <polygon
        fill={BRAND_BLUE}
        points="76.05 22.32 64.9 11.16 76.06 0 98.37 0 76.05 22.32"
      />
      <polygon
        fill="currentColor"
        points="22.32 22.32 33.47 11.16 22.31 0 0 0 22.32 22.32"
      />
      <polygon
        fill={BRAND_BLUE}
        points="22.32 27.37 33.47 38.52 22.31 49.69 0 49.69 22.32 27.37"
      />
      <polygon
        fill="currentColor"
        points="76.05 27.37 64.9 38.52 76.06 49.69 98.37 49.69 76.05 27.37"
      />
    </svg>
  );
}

export default ZunoxMark;
